import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  AdminTemplateCreatePayload,
  AdminTemplateMutationResponse,
  AdminTemplateUpdatePayload,
  InfobipImportResult,
  InfobipTemplateRef,
  WhatsappTemplateCreationInput,
  WhatsappTemplateLinesQuery,
  WhatsappTemplateLinesPage,
  WhatsappTemplateConfiguration,
} from '../../types/adminTemplates';
import type {
  ConsultaDeTemplatesWhatsapp,
  ConsultaDeUsosDeTemplateWhatsapp,
  DefinicaoDeFinalidadeInput,
  FalhaDaDefinicaoDeFinalidade,
  FalhaDaExclusaoWhatsapp,
  PaginaDeTemplatesWhatsapp,
  PaginaDeUsosDeTemplateWhatsapp,
  ResultadoDaGestaoWhatsapp,
  ResultadoDaExclusaoWhatsapp,
} from '../../types/templatesWhatsappGestao';
import {
  buildWhatsappTemplateCreatePayload,
  groupInfobipRefsByLine,
} from '../../utils/whatsappTemplates';
import { ErroDaGestaoDeTemplateWhatsapp, falhaDaDefinicaoDeFinalidade } from './erros';
import { adminTemplateServices } from './services';

export const excluirTemplateWhatsapp = createAsyncThunk<
  ResultadoDaExclusaoWhatsapp,
  string,
  { state: RootState; rejectValue: FalhaDaExclusaoWhatsapp }
>(
  'adminTemplates/excluirWhatsappDaGestao',
  async (templateId, { rejectWithValue }) => {
    try {
      return await adminTemplateServices.excluirWhatsappDaGestao(templateId);
    } catch (error) {
      return rejectWithValue({
        mensagem:
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Não foi possível excluir o template.',
        codigo: error instanceof ErroDaGestaoDeTemplateWhatsapp ? error.codigo : null,
      });
    }
  },
  {
    condition: (templateId, { getState }) =>
      getState().adminTemplates.gestaoWhatsapp.exclusoes[templateId]?.status !== 'loading',
  },
);

export const getWhatsappTemplateConfiguration = createAsyncThunk<
  WhatsappTemplateConfiguration,
  void
>('adminTemplates/getWhatsappConfiguration', async () =>
  adminTemplateServices.getWhatsappConfiguration(),
);

export const listWhatsappTemplateLines = createAsyncThunk<
  WhatsappTemplateLinesPage,
  WhatsappTemplateLinesQuery
>('adminTemplates/listWhatsappLines', async (query) =>
  adminTemplateServices.listWhatsappLines(query),
);

export const listarTemplatesWhatsappDaGestao = createAsyncThunk<
  PaginaDeTemplatesWhatsapp,
  ConsultaDeTemplatesWhatsapp
>('adminTemplates/listarWhatsappDaGestao', async (consulta, { signal }) =>
  adminTemplateServices.listarWhatsappDaGestao(consulta, signal),
);

/**
 * O 409 volta como valor tipado, e não como erro genérico: a tela precisa dos usos que ele traz
 * para listar, com link, onde o template é usado com outro tipo de telefone.
 */
export const definirFinalidadeDoTemplateWhatsapp = createAsyncThunk<
  ResultadoDaGestaoWhatsapp,
  DefinicaoDeFinalidadeInput,
  { rejectValue: FalhaDaDefinicaoDeFinalidade }
>('adminTemplates/definirFinalidadeWhatsapp', async (input, { rejectWithValue }) => {
  try {
    return await adminTemplateServices.definirFinalidadeWhatsapp(input);
  } catch (error) {
    return rejectWithValue(
      falhaDaDefinicaoDeFinalidade(error, 'Não foi possível definir a finalidade.'),
    );
  }
});

export const listarUsosDaTrocaDeFinalidade = createAsyncThunk<
  PaginaDeUsosDeTemplateWhatsapp,
  ConsultaDeUsosDeTemplateWhatsapp
>('adminTemplates/listarUsosDaTrocaDeFinalidade', async (consulta, { signal }) =>
  adminTemplateServices.listarUsosWhatsapp(consulta, signal),
);

export const redistribuirTemplateWhatsapp = createAsyncThunk<ResultadoDaGestaoWhatsapp, string>(
  'adminTemplates/redistribuirWhatsapp',
  async (templateId) => adminTemplateServices.redistribuirWhatsapp(templateId),
);

export const getAdminTemplateById = createAsyncThunk(
  'adminTemplates/getById',
  async ({ id, escritorioId }: { id: string; escritorioId?: string }) => {
    return await adminTemplateServices.getById(id, escritorioId);
  },
);

export const createAdminTemplate = createAsyncThunk(
  'adminTemplates/create',
  async (payload: AdminTemplateCreatePayload) => {
    return await adminTemplateServices.create(payload);
  },
);

export const updateAdminTemplate = createAsyncThunk(
  'adminTemplates/update',
  async ({ id, payload }: { id: string; payload: AdminTemplateUpdatePayload }) => {
    return await adminTemplateServices.update(id, payload);
  },
);

export const publishAdminTemplate = createAsyncThunk(
  'adminTemplates/publish',
  async ({ id, escritorioId }: { id: string; escritorioId?: string }) => {
    return await adminTemplateServices.publish(id, escritorioId);
  },
);

/**
 * Criar e publicar numa ação só: o template de fluxo só entra no catálogo do nó depois de
 * publicado, e um rascunho órfão aqui não tem tela para ser publicado depois.
 */
export const createAndPublishWhatsappTemplate = createAsyncThunk<
  AdminTemplateMutationResponse,
  WhatsappTemplateCreationInput
>('adminTemplates/createAndPublishWhatsapp', async (input) => {
  const created = await adminTemplateServices.create(buildWhatsappTemplateCreatePayload(input));
  return await adminTemplateServices.publish(created.data.id, input.escritorioId);
});

export const listInfobipTemplates = createAsyncThunk(
  'adminTemplates/listInfobip',
  async (telefoneInternoId?: string) => {
    return await adminTemplateServices.listInfobip(telefoneInternoId);
  },
);

export const importInfobipTemplates = createAsyncThunk<InfobipImportResult, InfobipTemplateRef[]>(
  'adminTemplates/importInfobip',
  async (refs) => {
    const results = await Promise.all(
      groupInfobipRefsByLine(refs).map(({ templateNames, telefoneInternoId }) =>
        adminTemplateServices.importInfobip(templateNames, telefoneInternoId),
      ),
    );
    return {
      imported: results.reduce((total, { data }) => total + data.imported, 0),
      skipped: results.reduce((total, { data }) => total + data.skipped, 0),
      templates: results.flatMap(({ data }) => data.templates),
    };
  },
);

export const syncInfobipTemplateButtons = createAsyncThunk(
  'adminTemplates/syncInfobipButtons',
  async (ref: InfobipTemplateRef) => {
    return await adminTemplateServices.syncInfobipButtons(
      [ref.name],
      ref.telefoneInternoId ?? undefined,
    );
  },
);
